import { useNavigate, useSearch } from "@tanstack/react-router";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { TierChip } from "@/components/cinch/TierChip";
import { AccountDetailContent } from "@/features/renewals/components/AccountDetailContent";
import { fmtCur, fmtCur2 } from "@/features/renewals/format";
import { cn } from "@/lib/utils";

import { ContactHistoryChart } from "./ContactHistoryChart";

import type { ExpansionSearch } from "..";
import type { ExpansionAccount, ExpansionCompany } from "../api";
import { fmtContacts } from "../lib";

/**
 * Account drawer for the expansion table, bound to /expansion:
 *
 *   ?account=<qbId>              → quick view (the overage math, line by line)
 *   ?account=<qbId>&expanded=1   → expanded (full account: billing + history)
 *
 * Same URL-driven convention as Upsell Ops; expanded reuses the shared
 * renewals account view.
 */
export function ExpansionAccountDrawer({ accounts }: { accounts: ExpansionAccount[] }) {
  const navigate = useNavigate();
  const search = useSearch({ from: "/expansion" }) as ExpansionSearch;
  const qbId = search.account ?? null;
  const expanded = !!search.expanded;
  const account = qbId != null ? (accounts.find((a) => a.qb_id === qbId) ?? null) : null;

  const go = (next: Partial<ExpansionSearch>) =>
    navigate({
      to: "/expansion",
      search: (prev: ExpansionSearch) => ({ ...prev, ...next }),
    });

  return (
    <EntityDrawer
      open={qbId != null}
      onOpenChange={(v) => {
        if (!v && !expanded) go({ account: undefined, expanded: undefined });
      }}
      expanded={expanded}
      onExpandedChange={(exp) => {
        if (qbId != null) go({ expanded: exp ? true : undefined });
      }}
      onClose={() => {
        if (qbId == null) return;
        if (expanded) go({ expanded: undefined });
        else go({ account: undefined, expanded: undefined });
      }}
      title={account?.name ?? "Account"}
      subtitle={qbId != null ? `QB-${qbId}` : ""}
    >
      {qbId != null &&
        (expanded ? (
          <div className="flex flex-col gap-5 p-4">
            {account && <ContactHistoryChart account={account} />}
            <CompanyBreakdown account={account} showBoth />
            <AccountDetailContent qbId={qbId} />
          </div>
        ) : (
          <OverageBreakdown account={account} />
        ))}
    </EntityDrawer>
  );
}

/**
 * The companies rolled up into the account's usage. Quick view shows only the
 * metric the contract prices on; the expanded view shows both customers and
 * emails. Every active, non-Sandbox company is listed — the ones that don't
 * count (Testing/Template) are dimmed and tagged, so a mislabeled or duplicate
 * company is easy to spot and fix at the source.
 */
function CompanyBreakdown({
  account,
  showBoth = false,
}: {
  account: ExpansionAccount | null;
  showBoth?: boolean;
}) {
  if (!account || account.companies.length === 0) {
    return (
      <div>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Companies
        </h3>
        <p className="text-sm text-muted-foreground">
          No active companies found for this account.
        </p>
      </div>
    );
  }
  const useEmail = account.uses_email_count;
  const metric = (c: ExpansionCompany) => (useEmail ? c.emails : c.customers);
  const counted = account.companies.filter((c) => c.counted);
  const totalContacts = counted.reduce((s, c) => s + metric(c), 0);

  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Companies counted ({counted.length})
      </h3>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-1.5 text-left font-medium">Company</th>
              {showBoth ? (
                <>
                  <th className="px-3 py-1.5 text-right font-medium">Customers</th>
                  <th className="px-3 py-1.5 text-right font-medium">Emails</th>
                </>
              ) : (
                <th className="px-3 py-1.5 text-right font-medium">
                  {useEmail ? "Emails" : "Customers"}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {account.companies.map((c) => (
              <tr
                key={c.company_id}
                className={cn(
                  "border-b border-border/50 last:border-0",
                  !c.counted && "opacity-55",
                )}
              >
                <td className="px-3 py-1.5">
                  <div className="font-medium">{c.name ?? c.company_id}</div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    {c.status}
                    {!c.counted && (
                      <span className="rounded border border-border px-1 py-px text-[9px] font-bold uppercase tracking-wider">
                        not counted
                      </span>
                    )}
                  </div>
                </td>
                {showBoth ? (
                  <>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {fmtContacts(c.customers)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {fmtContacts(c.emails)}
                    </td>
                  </>
                ) : (
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {fmtContacts(metric(c))}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border font-semibold">
              <td className="px-3 py-1.5">Total counted</td>
              {showBoth ? (
                <>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {fmtContacts(counted.reduce((s, c) => s + c.customers, 0))}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {fmtContacts(counted.reduce((s, c) => s + c.emails, 0))}
                  </td>
                </>
              ) : (
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {fmtContacts(totalContacts)}
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        All active, non-Sandbox companies are listed. Dimmed rows
        (Testing/Template) aren&apos;t counted. Spot a duplicate or mislabeled
        company here, then inactivate/relabel it at the source in Cinch.
      </p>
    </div>
  );
}

function Line({
  label,
  value,
  sub,
  strong,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <div>
        <div className={cn("text-sm", strong && "font-semibold")}>{label}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </div>
      <div className={cn("text-sm tabular-nums", strong && "text-base font-bold")}>{value}</div>
    </div>
  );
}

function OverageBreakdown({ account }: { account: ExpansionAccount | null }) {
  if (!account) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        This account isn't in the current expansion dataset (it may not have
        billed the platform item in the anchor month).
      </p>
    );
  }
  const a = account;
  return (
    <div className="flex flex-col gap-5 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <TierChip tier={a.tier} />
        <span className="text-sm font-semibold tabular-nums">{fmtCur(a.arr)} ARR</span>
        <span className="text-xs text-muted-foreground">CSM: {a.csm_label ?? "Unassigned"}</span>
      </div>

      {(a.flags.no_contract || a.flags.no_allotment || a.flags.no_block_price) && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
          {a.flags.no_contract
            ? "No New/Replacement contract found — there is nothing to bill against. Check the contracts feature."
            : a.flags.no_allotment
              ? "The governing contract has no contact allotment (initial contacts = 0), so overage can't be computed."
              : "The governing contract has no $/10k price — overage contacts are visible but unpriced."}
        </div>
      )}

      <div>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Contract
        </h3>
        <div className="divide-y divide-border rounded-md border border-border px-3">
          <Line
            label="Contracted contacts"
            sub={
              a.flags.no_contract
                ? undefined
                : `${a.contract_type} · signed ${a.contract_signed_at ?? "—"}${
                    a.upsell_contacts > 0
                      ? ` · incl. ${fmtContacts(a.upsell_contacts)} from upsell`
                      : ""
                  }`
            }
            value={fmtContacts(a.contracted_contacts)}
          />
          <Line
            label="Base fee"
            value={a.base_fee != null ? fmtCur2(a.base_fee) : "—"}
          />
          <Line
            label="Per additional 10k contacts"
            value={a.ten_k_more != null ? fmtCur2(a.ten_k_more) : "—"}
          />
        </div>
      </div>

      <div>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Usage ({a.uses_email_count ? "emails" : "contacts"} basis)
        </h3>
        <div className="divide-y divide-border rounded-md border border-border px-3">
          <Line
            label="Current usage"
            sub={`${fmtContacts(a.customers)} contacts · ${fmtContacts(a.emails)} emails · ${a.companies_count} compan${a.companies_count === 1 ? "y" : "ies"}`}
            value={fmtContacts(a.usage)}
          />
          <Line
            label="Over allotment"
            sub={
              a.overage_contacts != null && a.overage_contacts > 0
                ? `${a.overage_blocks} started 10k block${a.overage_blocks === 1 ? "" : "s"}`
                : undefined
            }
            value={
              <span className={cn((a.overage_contacts ?? 0) > 0 && "font-semibold text-orange")}>
                {fmtContacts(a.overage_contacts)}
              </span>
            }
          />
          <Line
            label="Overage cost"
            value={a.overage_cost != null ? fmtCur2(a.overage_cost) : "—"}
          />
        </div>
      </div>

      <CompanyBreakdown account={a} />

      <div>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          The gap
        </h3>
        <div className="divide-y divide-border rounded-md border border-border px-3">
          <Line
            label="Should bill per contract"
            sub="Base fee + overage"
            value={a.contracted_total != null ? fmtCur2(a.contracted_total) : "—"}
          />
          <Line label="Currently billed" sub="Platform item, anchor month" value={fmtCur2(a.currently_billed)} />
          <Line
            label="To bill"
            strong
            value={
              a.amount_to_bill == null ? (
                "—"
              ) : (
                <span
                  className={cn(
                    a.amount_to_bill > 0.005
                      ? "text-success"
                      : a.amount_to_bill < -0.005
                        ? "text-destructive"
                        : undefined,
                  )}
                >
                  {fmtCur2(a.amount_to_bill)}
                </span>
              )
            }
          />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Expand the drawer (top-right) for the full account view — contracts,
        companies, and the monthly billing timeline.
      </p>
    </div>
  );
}
